import { Box, Stack, Tooltip, Typography } from "@mui/material"
import { If, Else } from "lib/@components/switch"
import { LocalizedMessage } from "lib/localization/localized-message"
import { Editable } from "lib/@components/editable"
import { Bound } from "lib/@components/binding/Bound"
import { MinHeightBox } from "lib/@components/minHeightBox"
import { HighlightBox } from "lib/@components/highlightBox"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { useTranslationContext } from "routes/schedule/plugins/translation/use-translation-context"
import { FrequencyFields } from "routes/schedule/tasks/frequency-fields"
import { useFrequencyOverride } from "routes/schedule/tasks/frequency-override"

export const ZERO_U_WORDS =
    "0U (Zero U) tasks which means the frequency that those tasks should be carried out is to be defined by the user based on a site-specific assessment."

export function getZeroUTooltip(translatedSkills) {
    return translatedSkills?._zeroUTooltip ?? ZERO_U_WORDS
}

export function Frequency({ sx, labelSx, className, ...props }) {
    const { task } = useBoundContext()
    const { translatedSkills } = useTranslationContext()

    if (!task?.frequency) return null

    return (
        <MinHeightBox
            ml={1}
            className={["frequency", className].filter(Boolean).join(" ")}
            data-cy="task-frequency"
            data-comment-part="frequency"
            {...props}
        >
            <Editable pb={0.4} pt={0.4} editor={<FrequencyEditor sx={sx} />}>
                <HighlightBox path="frequency">
                    <Tooltip
                        enterDelay={300}
                        enterNextDelay={300}
                        title={
                            <If value={task.frequency.period[0] === "U"}>
                                <Typography component="span" variant="body2">
                                    {getZeroUTooltip(translatedSkills)}
                                </Typography>
                                <Else>
                                    <Stack>
                                        <Typography variant="caption" component="div">
                                            <LocalizedMessage template="Frequency" />
                                        </Typography>
                                        <Typography variant="body2">
                                            <LocalizedMessage
                                                template="Every {interval} {period}."
                                                values={{
                                                    interval: task.frequency.interval,
                                                    period: task.frequency.period,
                                                }}
                                            />
                                        </Typography>
                                    </Stack>
                                </Else>
                            </If>
                        }
                    >
                        <Box borderRadius={1} minWidth={"6.5em"} p={0.5} pl={1} pr={1} sx={labelSx}>
                            <If not truthy={task.frequency.period[0] === "U"}>
                                <strong>
                                    <LocalizedMessage template="Every" />{" "}
                                </strong>
                            </If>
                            <span>
                                {task.frequency.interval}
                                {task.frequency.period[0]}
                            </span>
                        </Box>
                    </Tooltip>
                </HighlightBox>
            </Editable>
        </MinHeightBox>
    )
}

export function FrequencyEditor({ sx, ...props }) {
    const { editTarget, markEdited, commit, tailoring } = useFrequencyOverride()
    // In tailoring mode the fields bind a DRAFT, and useBoundValue fires onChange on every write —
    // including the mount-time normalising effects in FrequencyFields (period coercion, code
    // derivation). Left to bubble, those writes reach the page's save chain and SAVE the regime on
    // editor OPEN, minting audit rows and re-render storms before the user has touched anything.
    // Returning false
    // stops Bound's chain; `commit` alone decides when the parent onChange runs, and only for a
    // real user edit. Master editors keep the ordinary chain — their targets ARE the record.
    return (
        <Bound target={editTarget} {...(tailoring ? { onChange: absorbDraftWrites } : {})}>
            <FrequencyFields {...props} sx={sx} markEdited={markEdited} commit={commit} />
        </Bound>
    )

    function absorbDraftWrites() {
        return false
    }
}
